(function (global) {
  'use strict';

  var LAYER_KEYS = {
    'Traffic|Page Handoff': 't3',
    'Traffic|Traffic Qualification': 't2',
    'Conversion|Offer Design': 'c1',
    'Conversion|Page Alignment': 'c2',
    'Conversion|Friction Removal': 'c3',
    'Retention|Purchase Loop': 'r2',
  };
  var STATUS_LEVEL = { pass: 0, warn: 1, fail: 2 };
  var STATUS_LABEL = { pass: 'PASS', warn: 'WARN', fail: 'FAIL' };
  var PILLAR_KEY = { Traffic: 't', Conversion: 'c', Retention: 'r' };

  function buildRequest(values) {
    var request = { storeUrl: String(values.storeUrl || '').trim() };
    var productUrl = String(values.productUrl || '').trim();
    var customerEmail = String(values.customerEmail || '').trim();

    if (productUrl) request.productUrl = productUrl;
    if (customerEmail) request.customer = { email: customerEmail };
    return request;
  }

  function layerKey(pillar, layer) {
    return LAYER_KEYS[String(pillar || '') + '|' + String(layer || '')] || null;
  }

  function parseResponse(payload) {
    if (!payload || payload.ok !== true || !payload.result) {
      var message = payload && typeof payload.message === 'string'
        ? payload.message
        : 'The X-ray could not read that store. Please try again.';
      var error = new Error(message);
      error.code = payload && payload.code ? payload.code : 'INVALID_RESPONSE';
      throw error;
    }
    return payload.result;
  }

  function isValidStoreUrl(value) {
    var raw = String(value || '').trim();
    if (!raw || /\s/.test(raw)) return false;
    try {
      var parsed = new URL(/^https?:\/\//i.test(raw) ? raw : 'https://' + raw);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        ? parsed.hostname.indexOf('.') > 0
        : false;
    } catch (_error) {
      return false;
    }
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function setConsole(consoleElement, prefix, detail) {
    if (!consoleElement) return;
    consoleElement.replaceChildren(document.createTextNode(prefix));
    if (detail) {
      var strong = element('strong', '', detail);
      consoleElement.appendChild(strong);
    }
  }

  function setSubmitLoading(button, loading) {
    if (!button) return;
    if (loading) {
      if (!button.dataset.idleLabel) {
        button.dataset.idleLabel = String(button.textContent || '').trim();
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.dataset.loadingLabel || 'Running the X-ray…';
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (button.dataset.idleLabel) button.textContent = button.dataset.idleLabel;
  }

  function setStatusClasses(node, status) {
    if (!node) return;
    node.classList.remove('pass', 'warn', 'fail', 'neutral');
    node.classList.add(status && STATUS_LEVEL[status] !== undefined ? status : 'neutral');
  }

  function sourceFor(check) {
    var id = String(check.id || '');
    if (id.indexOf('ai.') === 0) return 'AI judgment';
    if (id.indexOf('crux.') === 0) return 'CrUX';
    if (id.indexOf('render.') === 0) return 'Mobile render';
    return 'Crawler';
  }

  function flattenChecks(result) {
    var seen = {};
    var checks = [];
    (result.pillars || []).forEach(function (pillar) {
      (pillar.checks || []).forEach(function (check) {
        var key = check.id || [check.pillar, check.layer, check.finding].join('|');
        if (seen[key]) return;
        seen[key] = true;
        checks.push(check);
      });
    });
    return checks;
  }

  function formatScanSummary(result) {
    var checks = flattenChecks(result || {});
    var verdictLayer = (result.pillars || []).find(function (pillar) {
      return result.verdict
        && pillar.pillar === result.verdict.pillar
        && pillar.layer === result.verdict.layer;
    });
    var summary = result.scanSummary || {
      checksRun: checks.length,
      issuesFound: checks.filter(function (check) { return check.status !== 'pass'; }).length,
      pagesAnalysed: (result.pagesAnalysed || []).length,
      verdictScore: verdictLayer ? verdictLayer.score : 100,
      verdictStatus: verdictLayer ? verdictLayer.status : 'pass',
      rendered: Boolean(result.rendered),
      aiEnriched: Boolean(result.aiEnriched),
    };
    var displayUrl = String(result.storeUrl || '').trim();
    try {
      var parsed = new URL(/^https?:\/\//i.test(displayUrl) ? displayUrl : 'https://' + displayUrl);
      displayUrl = parsed.hostname + (parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, ''));
    } catch (_error) {
      // Keep the server-provided value when it cannot be shortened safely.
    }

    var statusLabel = summary.verdictStatus === 'fail'
      ? 'leaking'
      : summary.verdictStatus === 'warn'
        ? 'warning'
        : 'healthy';
    var source = [result.shopify ? 'Shopify' : 'Ecommerce'];
    source.push(summary.rendered ? 'mobile render' : 'page scan');
    if (summary.aiEnriched) source.push('AI enriched');
    source.push(result.cached ? 'recent result' : 'fresh result');

    return {
      page: displayUrl,
      coverage: summary.checksRun + ' checks · ' + summary.issuesFound + ' issues found',
      health: summary.verdictScore + '/100 · ' + statusLabel,
      source: source.join(' · '),
    };
  }

  function renderScanSummary(engine, result) {
    var container = engine.querySelector('[data-bd-xray-scan-summary]');
    if (!container) return;
    var summary = formatScanSummary(result);
    var values = {
      page: summary.page,
      coverage: summary.coverage,
      health: summary.health,
      source: summary.source,
    };
    Object.keys(values).forEach(function (key) {
      var node = container.querySelector('[data-bd-xray-summary-' + key + ']');
      if (node) node.textContent = values[key];
    });
    container.hidden = false;
  }

  function renderVerdictEvidence(engine, result) {
    var container = engine.querySelector('[data-bd-xray-verdict-evidence]');
    var list = engine.querySelector('[data-bd-xray-verdict-evidence-list]');
    if (!container || !list) return;
    var evidence = Array.isArray(result.verdictEvidence)
      ? result.verdictEvidence
      : flattenChecks(result).filter(function (check) {
          return result.verdict
            && check.pillar === result.verdict.pillar
            && check.layer === result.verdict.layer;
        });
    evidence = evidence.filter(function (check) {
      return !result.topIssue || check.finding !== result.topIssue.finding;
    }).slice(0, 2);

    list.replaceChildren();
    evidence.forEach(function (check) {
      var item = element('li');
      item.appendChild(element('span', 'evidence-status ' + check.status, STATUS_LABEL[check.status] || 'CHECK'));
      item.appendChild(element('span', '', check.finding));
      list.appendChild(item);
    });
    container.hidden = evidence.length === 0;
  }

  function renderCheckRows(engine, result) {
    var grid = engine.querySelector('[data-bd-xray-checks]');
    var badge = engine.querySelector('[data-bd-xray-checks-badge]');
    var note = engine.querySelector('[data-bd-xray-checks-note]');
    if (!grid) return;

    grid.replaceChildren();
    var checks = flattenChecks(result);
    if (!checks.length) {
      var gatedRow = element('div', 'checkrow');
      gatedRow.appendChild(element('span', 'cstat neutral'));
      var gatedMain = element('div', 'cmain');
      gatedMain.appendChild(element('b', '', 'Live scan complete'));
      gatedMain.appendChild(element('span', '', 'Individual checks are included in the free account teardown.'));
      gatedRow.appendChild(gatedMain);
      grid.appendChild(gatedRow);
      if (badge) badge.textContent = 'scan complete · headline view';
    } else {
      checks.forEach(function (check) {
        var row = element('div', 'checkrow');
        row.appendChild(element('span', 'cstat ' + check.status));

        var main = element('div', 'cmain');
        main.appendChild(element('b', '', check.finding));
        main.appendChild(element('span', '', check.status === 'pass' ? 'No action needed.' : check.fix));
        row.appendChild(main);

        var meta = element('div', 'cmeta');
        meta.appendChild(element('span', 'csrc', sourceFor(check)));
        meta.appendChild(element('span', 'cmap', check.pillar + ' · ' + check.layer));
        row.appendChild(meta);
        grid.appendChild(row);
      });
      if (badge) badge.textContent = 'scan complete · ' + checks.length + ' checks';
    }

    if (note) {
      note.textContent = result.caveat || 'Live output from the public storefront. No store access was used.';
    }
  }

  function setVerdictHeadline(node, verdict) {
    if (!node || !verdict) return;
    var full = String(verdict.headline || 'Your Store X-ray is ready.');
    var area = String(verdict.pillar || '') + ' · ' + String(verdict.layer || '');
    var index = area.trim() ? full.indexOf(area) : -1;
    if (index < 0) {
      node.textContent = full;
      return;
    }

    node.replaceChildren(
      document.createTextNode(full.slice(0, index)),
      element('em', '', area),
      document.createTextNode(full.slice(index + area.length)),
    );
  }

  function worstStatusForPillar(result, pillarName) {
    var levels = (result.pillars || [])
      .filter(function (pillar) { return pillar.pillar === pillarName; })
      .map(function (pillar) { return STATUS_LEVEL[pillar.status] || 0; });
    if (!levels.length) return null;
    var level = Math.max.apply(null, levels);
    return level === 2 ? 'fail' : level === 1 ? 'warn' : 'pass';
  }

  function renderPillarTiles(engine, result) {
    Object.keys(PILLAR_KEY).forEach(function (pillarName) {
      var tile = engine.querySelector('.ptile[data-pillar="' + PILLAR_KEY[pillarName] + '"]');
      if (!tile) return;
      var label = tile.querySelector('.ps span');
      var status = worstStatusForPillar(result, pillarName);

      if (!status && result.verdict && result.verdict.pillar === pillarName) {
        setStatusClasses(tile, 'warn');
        if (label) label.textContent = 'Fix first';
        return;
      }

      setStatusClasses(tile, status);
      if (label) {
        label.textContent = status === 'fail'
          ? 'Leaking'
          : status === 'warn'
            ? 'Warning'
            : status === 'pass'
              ? 'Healthy'
              : 'Not scored';
      }
    });
  }

  function renderTaste(engine, result) {
    var taste = engine.querySelector('[data-bd-xray-taste]');
    if (!taste) return;
    taste.hidden = false;

    setVerdictHeadline(engine.querySelector('[data-bd-xray-verdict]'), result.verdict);
    var dot = engine.querySelector('[data-bd-xray-verdict-dot]');
    if (dot) dot.style.background = 'var(--warn)';

    var finding = engine.querySelector('[data-bd-xray-finding]');
    var fix = engine.querySelector('[data-bd-xray-fix]');
    var more = engine.querySelector('[data-bd-xray-more]');
    if (finding) finding.textContent = result.topIssue.finding;
    if (fix) fix.textContent = result.topIssue.fix;
    if (more) {
      more.textContent = result.moreCount === 0
        ? 'No other issues'
        : result.moreCount + (result.moreCount === 1 ? ' more issue' : ' more issues');
    }
    renderScanSummary(engine, result);
    renderVerdictEvidence(engine, result);
    renderPillarTiles(engine, result);
  }

  function renderLayerCells(engine, result) {
    engine.querySelectorAll('.cell[data-layer]').forEach(function (cell) {
      setStatusClasses(cell, null);
      cell.classList.remove('first');
      var label = cell.querySelector('.st span');
      if (label) label.textContent = 'NOT SCORED';
    });

    (result.pillars || []).forEach(function (pillar) {
      var key = layerKey(pillar.pillar, pillar.layer);
      if (!key) return;
      var cell = engine.querySelector('.cell[data-layer="' + key + '"]');
      if (!cell) return;
      setStatusClasses(cell, pillar.status);
      var label = cell.querySelector('.st span');
      if (label) label.textContent = STATUS_LABEL[pillar.status] || 'NOT SCORED';
    });

    if (result.verdict) {
      var firstKey = layerKey(result.verdict.pillar, result.verdict.layer);
      var firstCell = firstKey && engine.querySelector('.cell[data-layer="' + firstKey + '"]');
      if (firstCell) firstCell.classList.add('first');
    }
  }

  function toolFor(engine, fix) {
    var layer = fix.layer;
    if (layer === 'Purchase Loop') {
      return { name: 'LTV Calculator', url: engine.dataset.toolRetentionUrl };
    }
    if (layer === 'Friction Removal') {
      return { name: 'Conversion Rate Calculator', url: engine.dataset.toolConversionUrl };
    }
    if (layer === 'Offer Design') {
      return { name: 'Offer Toolkit', url: engine.dataset.toolOfferUrl };
    }
    return { name: 'Temperature-Match Checker', url: engine.dataset.toolTemperatureUrl };
  }

  function renderFixFirst(engine, result) {
    var list = engine.querySelector('[data-bd-xray-fix-first]');
    if (!list) return;
    list.replaceChildren(element('li', 't3h', '// fix-first, ranked by impact'));

    if (!result.fixFirst || !result.fixFirst.length) {
      var emptyItem = element('li');
      var emptyBody = element('div', 't3b');
      emptyBody.appendChild(element('b', '', 'Nothing urgent to fix'));
      emptyBody.appendChild(element('p', '', 'The current diagnostic did not find a significant issue.'));
      emptyItem.appendChild(emptyBody);
      list.appendChild(emptyItem);
      return;
    }

    result.fixFirst.forEach(function (fix, index) {
      var item = element('li');
      item.appendChild(element('span', 'rk', '0' + (index + 1)));

      var body = element('div', 't3b');
      body.appendChild(element('b', '', fix.pillar + ' · ' + fix.layer));
      body.appendChild(element('p', '', fix.fix));
      var tool = toolFor(engine, fix);
      var link = element('a', '', 'Fix it with the ' + tool.name + ' →');
      link.href = tool.url || '#';
      body.appendChild(link);
      item.appendChild(body);
      item.appendChild(element('span', 'sv ' + fix.status, STATUS_LABEL[fix.status] || 'CHECK'));
      list.appendChild(item);
    });
  }

  function renderLeak(engine, result) {
    var row = engine.querySelector('[data-bd-xray-leak-row]');
    if (!row) return;
    if (!result.leakEstimate || !Number.isFinite(result.leakEstimate.monthly)) {
      row.hidden = true;
      return;
    }

    row.hidden = false;
    var value = row.querySelector('[data-bd-xray-leak]');
    if (value) {
      value.textContent = '~$' + Math.round(result.leakEstimate.monthly).toLocaleString('en-NZ') + '/mo';
    }
  }

  function renderGate(engine, result) {
    var gated = engine.querySelector('[data-bd-xray-gated]');
    var inner = engine.querySelector('[data-bd-xray-gated-inner]');
    if (!gated || !inner) return;
    gated.hidden = false;

    var isFull = Array.isArray(result.pillars) && Array.isArray(result.fixFirst);
    gated.classList.toggle('open', isFull);
    if (isFull) {
      inner.removeAttribute('aria-hidden');
      renderLayerCells(engine, result);
      renderFixFirst(engine, result);
      renderLeak(engine, result);
    } else {
      renderLayerCells(engine, { pillars: [] });
      var list = engine.querySelector('[data-bd-xray-fix-first]');
      if (list) {
        list.replaceChildren(element('li', 't3h', '// sign in to reveal the ranked fixes'));
      }
      var leak = engine.querySelector('[data-bd-xray-leak-row]');
      if (leak) leak.hidden = true;
      inner.setAttribute('aria-hidden', 'true');
    }
  }

  function renderResult(engine, result) {
    renderCheckRows(engine, result);
    renderTaste(engine, result);
    renderGate(engine, result);
  }

  function init(documentRoot) {
    var doc = documentRoot || global.document;
    if (!doc || !doc.querySelector) return;

    var hero = doc.querySelector('[data-bd-xray-hero]');
    var engine = doc.querySelector('[data-bd-xray-engine]');
    if (!hero || !engine || hero.dataset.bdXrayReady === 'true') return;
    hero.dataset.bdXrayReady = 'true';
    hero.classList.add('has-js');

    var form = hero.querySelector('[data-bd-xray-form]');
    var input = hero.querySelector('[data-bd-xray-store-url]');
    var productInput = hero.querySelector('[data-bd-xray-product-url]');
    var consoleElement = hero.querySelector('[data-bd-xray-console]');
    var stage = hero.querySelector('[data-bd-xray-stage]');
    var stageAddress = hero.querySelector('[data-bd-xray-stage-address]');
    var stageStatus = hero.querySelector('[data-bd-xray-stage-status]');
    var submit = form && form.querySelector('[type="submit"]');
    var reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timers = [];
    var runId = 0;

    function clearProgress() {
      timers.forEach(global.clearTimeout);
      timers = [];
    }

    function setResultsState(state) {
      var results = engine.querySelector('[data-bd-xray-results]');
      engine.dataset.scanState = state;
      if (results) results.setAttribute('aria-busy', state === 'scanning' ? 'true' : 'false');
    }

    function loading(storeUrl) {
      clearProgress();
      var taste = engine.querySelector('[data-bd-xray-taste]');
      var gated = engine.querySelector('[data-bd-xray-gated]');
      var checksBadge = engine.querySelector('[data-bd-xray-checks-badge]');
      var checksNote = engine.querySelector('[data-bd-xray-checks-note]');
      if (taste) taste.hidden = true;
      if (gated) gated.hidden = true;
      if (checksBadge) checksBadge.textContent = 'live scan · running';
      if (checksNote) checksNote.textContent = 'Your new live checks will replace these preview rows as soon as the scan finishes.';
      if (stageAddress) stageAddress.textContent = storeUrl;
      if (stageStatus) stageStatus.textContent = 'scanning';
      if (stage) {
        stage.classList.remove('idle', 'done');
        stage.classList.add('scanning');
      }
      setSubmitLoading(submit, true);
      setConsole(consoleElement, '// fetching storefront…');
      setResultsState('scanning');

      [
        [2200, '// reading mobile, copy and structure…'],
        [6500, '// checking traffic, conversion and retention…'],
        [11000, '// scoring the live signals…'],
      ].forEach(function (step) {
        timers.push(global.setTimeout(function () {
          setConsole(consoleElement, step[1]);
        }, step[0]));
      });
    }

    function finish(status) {
      clearProgress();
      if (stageStatus) stageStatus.textContent = status;
      if (stage) {
        stage.classList.remove('scanning', 'idle', 'done');
        stage.classList.add(status === 'done' ? 'done' : 'idle');
      }
      setSubmitLoading(submit, false);
      setResultsState(status === 'done' ? 'complete' : 'error');
    }

    function scrollToEngine() {
      if (!engine.scrollIntoView) return;
      engine.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }

    async function run(storeUrl) {
      var currentRun = ++runId;
      var url = String(storeUrl || '').trim();
      if (!isValidStoreUrl(url)) {
        setConsole(consoleElement, '// check the URL. ', 'Use a full store domain such as yourstore.com.');
        if (input) input.focus();
        return;
      }

      if (global.history && global.history.replaceState) {
        var nextUrl = new URL(global.location.href);
        nextUrl.searchParams.set('store', url);
        global.history.replaceState({}, '', nextUrl.toString());
      }

      loading(url);
      scrollToEngine();
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeout = controller && global.setTimeout(function () { controller.abort(); }, 35000);

      try {
        var response = await global.fetch(engine.dataset.endpoint || '/apps/store-xray/xray', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(buildRequest({
            storeUrl: url,
            productUrl: productInput && productInput.value,
            customerEmail: engine.dataset.customerEmail,
          })),
          signal: controller ? controller.signal : undefined,
        });
        var payload;
        try {
          payload = await response.json();
        } catch (_error) {
          throw new Error('The Store X-ray connection is not ready yet. Please try again shortly.');
        }
        var result = parseResponse(payload);
        if (currentRun !== runId) return;
        renderResult(engine, result);
        finish('done');
        setConsole(consoleElement, '// done. ', result.cached ? 'Live result loaded from the recent scan.' : 'Your live storefront has been analysed.');
      } catch (error) {
        if (currentRun !== runId) return;
        var message = error && error.name === 'AbortError'
          ? 'The scan took too long. Please try again.'
          : error.message;
        var checksBadge = engine.querySelector('[data-bd-xray-checks-badge]');
        var checksNote = engine.querySelector('[data-bd-xray-checks-note]');
        if (checksBadge) checksBadge.textContent = 'scan stopped · no new result';
        if (checksNote) checksNote.textContent = 'No new live result was loaded. Check the store URL and run the X-ray again.';
        finish('error');
        setConsole(consoleElement, '// scan stopped. ', message);
      } finally {
        if (timeout) global.clearTimeout(timeout);
      }
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        run(input && input.value);
      });
    }

    if (input && global.location && global.location.search) {
      var prefilledStore = new URLSearchParams(global.location.search).get('store');
      if (prefilledStore && isValidStoreUrl(prefilledStore)) {
        input.value = prefilledStore;
        global.setTimeout(function () { run(prefilledStore); }, 120);
      } else if (stage) {
        stage.classList.add('idle');
      }
    }
  }

  global.BetterDigitalStoreXray = {
    buildRequest: buildRequest,
    formatScanSummary: formatScanSummary,
    init: init,
    isValidStoreUrl: isValidStoreUrl,
    layerKey: layerKey,
    parseResponse: parseResponse,
    setSubmitLoading: setSubmitLoading,
  };

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () { init(global.document); });
    } else {
      init(global.document);
    }
    global.document.addEventListener('shopify:section:load', function () { init(global.document); });
  }
})(typeof window !== 'undefined' ? window : globalThis);
